import { useState, useEffect } from "react";
import { X, Smartphone, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

const DISMISSED_KEY = "myFitBeats_installPromptDismissed";

interface UserData {
  hideInstallPrompt?: boolean;
}

export function InstallPromptBanner() {
  const { isInstallable, isInstalled, isIOS, isStandalone, promptInstall, canPrompt } = useInstallPrompt();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [localDismissed, setLocalDismissed] = useState(true);

  const { data: user } = useQuery<UserData>({
    queryKey: ["/api/auth/user"],
    staleTime: 5 * 60 * 1000,
  });

  const dismissMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("PATCH", "/api/user/preferences", { hideInstallPrompt: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    },
  });

  useEffect(() => {
    const dismissed = localStorage.getItem(DISMISSED_KEY);
    setLocalDismissed(dismissed === "true");
  }, []);

  const isDismissed = user?.hideInstallPrompt || localDismissed;

  const handleInstall = async () => {
    if (canPrompt) {
      const success = await promptInstall();
      if (success) {
        toast({
          title: "App installed!",
          description: "myFitBeats has been added to your home screen",
        });
      }
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, "true");
    setLocalDismissed(true);
    dismissMutation.mutate();
  };

  if (isInstalled || isStandalone || isDismissed) {
    return null;
  }

  const showIOSInstructions = () => {
    toast({
      title: "Add to Home Screen",
      description: "Tap the Share button in your browser, then select 'Add to Home Screen'",
      duration: 8000,
    });
  };

  if (isIOS && isInstallable) {
    return (
      <div 
        className="mx-4 mt-2 px-3 py-2 rounded-lg bg-muted/50 border border-border/50 cursor-pointer active:bg-muted/70" 
        data-testid="card-install-prompt"
        onClick={showIOSInstructions}
      >
        <div className="flex items-center gap-2">
          <Smartphone className="h-4 w-4 text-muted-foreground shrink-0" />
          <p className="text-xs text-muted-foreground flex-1">
            Tap for install instructions
          </p>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              handleDismiss();
            }}
            data-testid="button-dismiss-install"
          >
            <X className="h-3 w-3 text-muted-foreground" />
          </Button>
        </div>
      </div>
    );
  }

  if (canPrompt) {
    return (
      <div className="mx-4 mt-2 px-3 py-2 rounded-lg bg-muted/50 border border-border/50" data-testid="card-install-prompt">
        <div className="flex items-center gap-2">
          <Download className="h-4 w-4 text-muted-foreground shrink-0" />
          <p className="text-xs text-muted-foreground flex-1">
            Install app for quick access
          </p>
          <Button
            size="sm"
            variant="secondary"
            className="h-6 text-xs px-2"
            onClick={handleInstall}
            data-testid="button-add-to-homescreen"
          >
            Install
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 shrink-0"
            onClick={handleDismiss}
            data-testid="button-dismiss-install"
          >
            <X className="h-3 w-3 text-muted-foreground" />
          </Button>
        </div>
      </div>
    );
  }

  return null;
}

export function useInstallPromptDismissed() {
  const [isDismissed, setIsDismissed] = useState(true);

  useEffect(() => {
    const dismissed = localStorage.getItem(DISMISSED_KEY);
    setIsDismissed(dismissed === "true");
  }, []);

  const resetDismissed = () => {
    localStorage.removeItem(DISMISSED_KEY);
    setIsDismissed(false);
  };

  return { isDismissed, resetDismissed };
}
